import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserStats } from '../../services/userService';
import { getInviteStats } from '../../services/inviteService';
import { getAlbumStats } from '../../services/albumService';
import { getBudgetStatus } from '../../services/budgetService';

const ADMIN_SESSION_KEY = 'vf_admin_session';
const ADMIN_ID = 'veeflix_admin';
const ADMIN_PASS = 'vfadmin008';

const checkAdminSession = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(ADMIN_SESSION_KEY) === '1';
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AdminDashboard] Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main style={{ minHeight: '100vh', background: '#111', color: '#fff', padding: '2rem' }}>
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '1.5rem', maxWidth: 600 }}>
            <h2 style={{ color: '#fca5a5', marginBottom: '1rem' }}>⚠️ Error Loading Dashboard</h2>
            <p style={{ color: '#fecaca', fontSize: '0.875rem', marginBottom: '1rem' }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Reload Page</button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminId, setAdminId] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [loginError, setLoginError] = useState('');

  const [stats, setStats] = useState({
    users: { total: 0, active: 0 },
    invites: { total: 0, active: 0, used: 0, expired: 0 },
    albums: { total: 0, totalPhotos: 0, totalVideos: 0, totalViews: 0 },
  });
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    try {
      const isAuth = checkAdminSession();
      if (isAuth) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('[AdminDashboard] Auth check failed:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadStats();
    }
  }, [isAuthenticated]);

  const loadStats = async () => {
    setRefreshing(true);
    try {
      const [userStats, inviteStats, albumStats, budget] = await Promise.all([
        getUserStats().catch(() => ({ total: 0, active: 0 })),
        getInviteStats().catch(() => ({ total: 0, active: 0, used: 0, expired: 0 })),
        getAlbumStats().catch(() => ({ totalAlbums: 0, totalPhotos: 0, totalVideos: 0, totalViews: 0 })),
        getBudgetStatus().catch(() => null),
      ]);

      setStats({
        users: userStats,
        invites: inviteStats,
        albums: {
          total: albumStats.totalAlbums,
          totalPhotos: albumStats.totalPhotos,
          totalVideos: albumStats.totalVideos,
          totalViews: albumStats.totalViews,
        },
      });
      setBudgetStatus(budget);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (adminId === ADMIN_ID && adminPass === ADMIN_PASS) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(ADMIN_SESSION_KEY, '1');
      }
      setIsAuthenticated(true);
      setLoginError('');
    } else {
      setLoginError('Invalid admin credentials');
    }
  };

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
    setIsAuthenticated(false);
    setAdminId('');
    setAdminPass('');
  };

  if (loading) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p>Loading...</p>
      </main>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <main style={{ minHeight: '100vh', background: '#111', color: '#fff', padding: '2rem' }}>
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '1.5rem', maxWidth: 600 }}>
            <h2 style={{ color: '#fca5a5', marginBottom: '1rem' }}>⚠️ Error Loading Dashboard</h2>
            <p style={{ color: '#fecaca', fontSize: '0.875rem', marginBottom: '1rem' }}>{error}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Reload Page</button>
          </div>
        </main>
      </ErrorBoundary>
    );
  }

  if (!isAuthenticated) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
        <div style={{ background: '#1a1a1a', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 400, border: '1px solid #333' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e50914' }}>VEEFLIX</span>
            <span style={{ color: '#666', marginLeft: '0.5rem' }}>Admin</span>
          </div>
          
          <p style={{ color: '#9ca3af', marginBottom: '1.5rem', fontSize: '0.875rem' }}>Sign in to access the admin panel</p>
          
          <form onSubmit={handleLogin}>
            <input type="text" value={adminId} onChange={(e) => setAdminId(e.target.value)} placeholder="Admin ID" style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', fontSize: '1rem' }} required />
            <input type="password" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} placeholder="Password" style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', fontSize: '1rem' }} required />
            <button type="submit" style={{ width: '100%', padding: '0.75rem', borderRadius: 6, border: 0, background: '#e50914', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>Sign in</button>
          </form>
          
          {loginError && <p style={{ color: '#ef4444', marginTop: '0.75rem', fontSize: '0.875rem', textAlign: 'center' }}>{loginError}</p>}
        </div>
        
        <p style={{ marginTop: '1.5rem', color: '#666' }}><Link to="/" style={{ color: '#9ca3af' }}>← Back to homepage</Link></p>
      </main>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    { id: 'media', label: 'Media Upload', path: '/admin/media' },
    { id: 'invites', label: 'Invites', path: '/admin/invites' },
    { id: 'users', label: 'Users', path: '/admin/users' },
    { id: 'albums', label: 'Albums', path: '/admin/albums' },
    { id: 'settings', label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <ErrorBoundary>
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff' }}>
        <header style={{ background: '#1a1a1a', borderBottom: '1px solid #333', padding: '0 1.5rem' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e50914' }}>VEEFLIX</span>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Admin</span>
            </div>
            
            <button onClick={handleLogout} style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #444', background: 'transparent', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer' }}>Logout</button>
          </div>
        </header>
        
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <Link key={tab.id} to={tab.path} style={{ padding: '0.5rem 0.9rem', borderRadius: 6, textDecoration: 'none', color: tab.id === 'dashboard' ? '#fff' : '#9ca3af', background: tab.id === 'dashboard' ? '#333' : 'transparent', fontSize: '0.875rem', fontWeight: 500 }}>
                {tab.label}
              </Link>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h1 style={{ margin: 0 }}>Dashboard</h1>
            <button onClick={loadStats} disabled={refreshing} style={{ padding: '0.5rem 1rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: refreshing ? 'not-allowed' : 'pointer' }}>
              {refreshing ? 'Refreshing...' : 'Refresh Stats'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#fff' }}>{stats.users.total}</div>
              <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.25rem' }}>Total Users</div>
            </div>
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b' }}>{stats.invites.active}</div>
              <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.25rem' }}>Active Invites</div>
            </div>
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#8b5cf6' }}>{stats.albums.total}</div>
              <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.25rem' }}>Albums</div>
            </div>
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981' }}>{stats.albums.totalPhotos}</div>
              <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.25rem' }}>Photos</div>
            </div>
          </div>

          {budgetStatus && (
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Budget Status</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div style={{ padding: '1rem', background: '#222', borderRadius: 8, borderLeft: budgetStatus.cloudinary?.alert ? '3px solid #ef4444' : '3px solid #16a34a' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Cloudinary</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{budgetStatus.cloudinary?.used || 0} / ${budgetStatus.cloudinary?.limit || 0}</div>
                  <div style={{ height: 4, background: '#333', borderRadius: 2, marginTop: '0.5rem', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${budgetStatus.cloudinary?.percent || 0}%`, background: budgetStatus.cloudinary?.alert ? '#ef4444' : '#16a34a' }} />
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#222', borderRadius: 8, borderLeft: budgetStatus.mux?.alert ? '3px solid #ef4444' : '3px solid #16a34a' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Mux</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{budgetStatus.mux?.used || 0} / ${budgetStatus.mux?.limit || 0}</div>
                  <div style={{ height: 4, background: '#333', borderRadius: 2, marginTop: '0.5rem', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${budgetStatus.mux?.percent || 0}%`, background: budgetStatus.mux?.alert ? '#ef4444' : '#16a34a' }} />
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#222', borderRadius: 8, borderLeft: budgetStatus.vercel?.alert ? '3px solid #ef4444' : '3px solid #16a34a' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Vercel</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{budgetStatus.vercel?.used || 0} / ${budgetStatus.vercel?.limit || 0}</div>
                  <div style={{ height: 4, background: '#333', borderRadius: 2, marginTop: '0.5rem', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${budgetStatus.vercel?.percent || 0}%`, background: budgetStatus.vercel?.alert ? '#ef4444' : '#16a34a' }} />
                  </div>
                </div>
              </div>
              {budgetStatus.cloudinary?.alert || budgetStatus.mux?.alert || budgetStatus.vercel?.alert ? (
                <p style={{ color: '#ef4444', marginTop: '1rem', fontSize: '0.875rem' }}>⚠️ Budget alert! One or more services are approaching their limit.</p>
              ) : null}
            </div>
          )}
          
          <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.25rem', marginBottom: '1rem' }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>Quick Links</h2>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Link to="/admin/media" style={{ padding: '0.6rem 1rem', background: '#333', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>Media Upload →</Link>
              <Link to="/admin/invites" style={{ padding: '0.6rem 1rem', background: '#333', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>Invites →</Link>
              <Link to="/admin/users" style={{ padding: '0.6rem 1rem', background: '#333', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>Users →</Link>
              <Link to="/admin/albums" style={{ padding: '0.6rem 1rem', background: '#333', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>Albums →</Link>
              <Link to="/admin/settings" style={{ padding: '0.6rem 1rem', background: '#333', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>Settings →</Link>
            </div>
          </div>
        </div>
      </main>
    </ErrorBoundary>
  );
}

export default AdminDashboard;